/**
 * Calendário de vencimento LST — semanal e mensal ocupam um único dia por ciclo.
 *
 * Âncora do dia (sem campo extra no banco):
 *  - sort_order > 0: semanal → sort_order % 7 (Seg=0…Dom=6); mensal → dia do mês (1–28)
 *  - fallback por period: início / meio / fim do turno
 */

import type { LstFrequency, LstPeriod, LstTemplate } from '../types';
import { weekdayIndexMon0 } from './lstService';

const WEEKDAY_BY_PERIOD: Record<LstPeriod, number> = {
  inicio: 0,
  meio: 2,
  fim: 4,
};

const MONTH_DAY_BY_PERIOD: Record<LstPeriod, number | 'last'> = {
  inicio: 1,
  meio: 15,
  fim: 'last',
};

function isDailyLike(freq: LstFrequency): boolean {
  return freq === 'horaria' || freq === 'diaria';
}

/** Dia da semana alvo (Seg=0 … Dom=6) para rotinas semanais. */
export function templateWeekdayMon0(template: Pick<LstTemplate, 'sort_order' | 'period'>): number {
  if (template.sort_order > 0) return template.sort_order % 7;
  return WEEKDAY_BY_PERIOD[template.period];
}

/** Dia do mês alvo (1–31) para rotinas mensais. */
export function templateMonthDay(
  template: Pick<LstTemplate, 'sort_order' | 'period'>,
  monthDates: string[],
): number {
  if (template.sort_order > 0) {
    const raw = ((template.sort_order - 1) % 28) + 1;
    const maxDay = monthDates.length > 0 ? parseInt(monthDates[monthDates.length - 1].split('-')[2], 10) : 28;
    return Math.min(raw, maxDay);
  }
  const mapped = MONTH_DAY_BY_PERIOD[template.period];
  if (mapped === 'last' && monthDates.length > 0) {
    return parseInt(monthDates[monthDates.length - 1].split('-')[2], 10);
  }
  return typeof mapped === 'number' ? mapped : 1;
}

/**
 * Indica se a rotina deve aparecer / ser cobrada na data informada.
 */
export function isTemplateDueOnDate(
  template: Pick<LstTemplate, 'frequency' | 'sort_order' | 'period'>,
  date: string,
  monthDates: string[] = [],
): boolean {
  if (isDailyLike(template.frequency)) return true;

  if (template.frequency === 'semanal') {
    return weekdayIndexMon0(date) === templateWeekdayMon0(template);
  }

  if (template.frequency === 'mensal') {
    const dayNum = parseInt(date.split('-')[2], 10);
    return dayNum === templateMonthDay(template, monthDates);
  }

  return true;
}

/** Primeira data de vencimento dentro de um intervalo (útil para semana). */
export function findDueDateInRange(
  template: Pick<LstTemplate, 'frequency' | 'sort_order' | 'period'>,
  dates: string[],
): string | null {
  return dates.find((d) => isTemplateDueOnDate(template, d, dates)) ?? null;
}
